import api from './api';
import { DeviceListResponse } from './devices';
import { ListUsersResponse } from './users';

// Company types
export interface Company {
  // Presence flag — the key itself is never returned by the API.
  has_nrf_cloud_api_key?: boolean;
  id: number;
  name: string;
  nrf_api_key?: string;
  nrf_team_id?: string;
  odoo_company_id?: string;
  odoo_contact_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyStats {
  total_devices: number;
  online_devices: number;
  offline_devices: number;
  total_users: number;
  active_users: number;
}

// Structured checkout defaults (migration 058) — prefill for the cart.
export interface CompanyAddressDefaults {
  default_shipping?: Record<string, string> | null;
  default_billing?: Record<string, string> | null;
}

export interface UpdateCompanyData {
  name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  nrf_api_key?: string;
  nrf_team_id?: string;
  odoo_company_id?: string;
  odoo_contact_id?: string;
}

export interface ConnectionTestResponse {
  success: boolean;
  message: string;
  device_count?: number;
}

export interface SyncResponse {
  success: boolean;
  synced: number;
  message: string;
}

export interface MqttStatus {
  enabled: boolean;
  connected: boolean;
  last_check?: string;
}

export interface BillingSettings {
  company_id: number;
  odoo_company_id?: string;
  odoo_contact_id?: string;
  billing_enabled: boolean;
}

export interface BillingSyncLog {
  id: number;
  company_id: number;
  status: 'success' | 'error';
  devices_billed: number;
  message?: string;
  created_at: string;
}

export interface ListOptions {
  page?: number;
  limit?: number;
}

export interface CompaniesListResponse {
  companies: Company[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Get all companies
 * Super Admin: all companies
 * Company Admin: own company only
 */
export async function getCompanies(): Promise<CompaniesListResponse> {
  const response = await api.get<CompaniesListResponse>('/companies');
  return response.data;
}

/**
 * Get company by ID
 */
export async function getCompany(id: number): Promise<Company> {
  const response = await api.get<Company>(`/companies/${id}`);
  return response.data;
}

/**
 * Get company statistics
 */
export async function getCompanyStats(id: number): Promise<CompanyStats> {
  const response = await api.get<CompanyStats>(`/companies/${id}/stats`);
  return response.data;
}

/**
 * Update company
 */
export async function updateCompany(id: number, data: UpdateCompanyData): Promise<Company> {
  const response = await api.patch<Company>(`/companies/${id}`, data);
  return response.data;
}

/**
 * Test nRF Cloud connection for company
 */
export async function testNrfCloudConnection(companyId: number): Promise<ConnectionTestResponse> {
  const response = await api.get<ConnectionTestResponse>(`/devices/sync/company/${companyId}/test`);
  return response.data;
}

/**
 * Sync devices for company
 */
export async function syncCompanyDevices(companyId: number): Promise<SyncResponse> {
  const response = await api.post<SyncResponse>(`/devices/sync/company/${companyId}`);
  return response.data;
}

/**
 * Get users for company
 */
export async function getCompanyUsers(companyId: number, options: ListOptions = {}): Promise<ListUsersResponse> {
  const params = new URLSearchParams();
  params.append('companyId', companyId.toString());
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.page) params.append('page', options.page.toString());

  const response = await api.get<ListUsersResponse>(`/users?${params.toString()}`);
  return response.data;
}

/**
 * Get devices for company
 */
export async function getCompanyDevices(companyId: number, options: Record<string, any> = {}): Promise<DeviceListResponse> {
  const params = { company_id: companyId, ...options };
  const response = await api.get<DeviceListResponse>('/devices', { params });
  return response.data;
}

/**
 * Get billing settings for company
 */
export async function getBillingSettings(companyId: number): Promise<BillingSettings> {
  const response = await api.get<BillingSettings>(`/billing/settings/${companyId}`);
  return response.data;
}

/**
 * Get billing sync logs for company
 */
export async function getBillingSyncLogs(companyId: number, options: ListOptions = {}): Promise<BillingSyncLog[]> {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit.toString());

  const response = await api.get<BillingSyncLog[]>(`/billing/odoo/logs?companyId=${companyId}&${params.toString()}`);
  return response.data;
}

/**
 * Get audit logs for company
 */
export async function getCompanyAuditLogs(companyId: number, options: ListOptions = {}): Promise<any> {
  const params = new URLSearchParams();
  params.append('companyId', companyId.toString());
  if (options.limit) params.append('limit', options.limit.toString());

  const response = await api.get(`/users/audit/logs?${params.toString()}`);
  return response.data;
}

/**
 * Sync billing for company
 */
export async function syncCompanyBilling(companyId: number): Promise<SyncResponse> {
  const response = await api.post<SyncResponse>(`/billing/sync/${companyId}`);
  return response.data;
}

/**
 * Get MQTT service status for company
 */
export async function getMqttStatus(companyId: number): Promise<MqttStatus> {
  const response = await api.get<MqttStatus>(`/companies/${companyId}/mqtt-status`);
  return response.data;
}
